/**
 * Shared `fetch` wrapper that attaches the current Clerk session token as a
 * `Authorization: Bearer …` header. Mirrors the existing FolderService
 * pattern: on 401 it refetches the token once and retries the request.
 *
 * The token getter is registered at app boot from RootStore so consumers
 * (hooks, services) don't need to thread the Clerk session through their
 * call stacks. Callers that inject their own token getter (e.g. services
 * built with constructor DI) can pass it as the third argument to override
 * the registered one.
 *
 * Falls back to a plain `fetch` when no getter is registered/passed — keeps
 * tests and unauth'd code paths working.
 */

type TokenGetter = () => Promise<string | undefined>;

let tokenGetter: TokenGetter | null = null;

export function setAuthTokenGetter(getter: TokenGetter | null): void {
  tokenGetter = getter;
}

export async function authenticatedFetch(
  url: string,
  init: RequestInit = {},
  getToken: TokenGetter | null = tokenGetter,
): Promise<Response> {
  const token = getToken ? await getToken() : undefined;
  const headers = mergeHeaders(init.headers, token);
  const response = await fetch(url, { ...init, headers });
  if (response.status !== 401 || !getToken) return response;

  // Token may have expired. Refresh once and retry.
  const fresh = await getToken();
  if (!fresh || fresh === token) return response;
  return fetch(url, { ...init, headers: mergeHeaders(init.headers, fresh) });
}

function mergeHeaders(
  initHeaders: HeadersInit | undefined,
  token: string | undefined,
): Record<string, string> {
  const merged: Record<string, string> = {};
  if (initHeaders) {
    if (initHeaders instanceof Headers) {
      initHeaders.forEach((value, key) => {
        merged[key] = value;
      });
    } else if (Array.isArray(initHeaders)) {
      for (const [key, value] of initHeaders) merged[key] = value;
    } else {
      Object.assign(merged, initHeaders);
    }
  }
  if (token) merged.Authorization = `Bearer ${token}`;
  return merged;
}
